/**
 * The only component in this build that renders an AI-written sentence.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  ONE RENDERER, SO THERE IS ONE PLACE THE CAVEAT CAN BE LOST
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `summary` (a stored insight) and `answer` (a chat turn) are the same kind of thing
 * — a model's paragraph about somebody's working day — and they are rendered by this
 * component and by nothing else. `insightRowFromChat` exists so a chat answer arrives
 * here rather than at a second, simpler renderer that would inevitably drop something.
 * The Executive-overview AI slot uses this component too, for the same reason: a tile
 * that trimmed the sentence to fit and left the caveat behind is the exact defect
 * this screen exists to avoid.
 *
 * `screens/ai/one-renderer.test.tsx` asserts that as a **set**: the files under
 * `web/src` that read a reading's `headline` are exactly this one.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE LIMITATIONS REGION IS UNCONDITIONAL, UNCOLLAPSIBLE AND ABOVE THE ADVICE
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Three properties, and each is asserted rather than assumed:
 *
 *  1. **Unconditional.** It is not inside any branch. Every variant of
 *     `InsightReading` reaches it, `unreadable` included — that variant carries
 *     `caveats` precisely so this stays true when nothing else about the row does.
 *     Its content is a `Caveats` tuple, non-empty by the type, so there is no
 *     "render nothing" path even for a row the server sent empty.
 *  2. **Uncollapsible.** No `<details>`, no `<summary>`, no `hidden`, no
 *     `aria-expanded`, no `aria-hidden`, no line clamp and no scroll container —
 *     anywhere in this subtree. A caveat behind one click is a caveat that was not
 *     read, and a caveat inside a 60px scroller is worse, because it looks handled.
 *  3. **Above the advice.** `possible_causes` and `recommended_actions` render
 *     *after* it in document order, so a reader who stops reading has stopped after
 *     the qualification rather than before it. The screen test asserts the ordering
 *     with `compareDocumentPosition`, not by eye.
 *
 * G16 is why the third one matters this month rather than in principle: a stale score
 * now reaches the AI path, the generator proceeds and says so, and the sentence it
 * appends lands in `limitations` **after validation** on the published, fallback and
 * failed paths alike. It is in the stored row whether or not the model complied. This
 * region is the only place it surfaces.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE STATUS NOTE IS NOT THE SAME SENTENCE FOUR TIMES
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `published`, `fallback`, `budgetRefused`, `superseded` and `unreadable` get five
 * different notes with five different silhouettes, because they need five different
 * actions from the reader. In particular **`budgetRefused` is not an error and is
 * not an empty result**: it says so in as many words, with `ban` rather than
 * `triangle-alert`, and it states that `ai_unavailable` is false on such a row so a
 * reader does not go looking for an outage that is not happening.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import {
  citesNothing,
  isCaveatSentinel,
  markersDisagree,
  readInsight,
  type Caveats,
  type Confidence,
  type EvidenceRef,
  type InsightReading,
} from '../../ai/insight';
import { evidenceTarget } from '../../ai/evidence';
import { Badge } from '../../states/Badge';
import { StatusNote } from '../../states/StatusNote';
import type { IconName, Severity } from '../../states/icons';

const TYPE_LABEL: Readonly<Record<string, MessageKey>> = {
  summary: 'm4.type.summary',
  trend_comparison: 'm4.type.trend_comparison',
  anomaly: 'm4.type.anomaly',
  cause_hypothesis: 'm4.type.cause_hypothesis',
  recommendation: 'm4.type.recommendation',
  workload_analysis: 'm4.type.workload_analysis',
  scoped_query: 'm4.type.scoped_query',
};

/**
 * Severity, silhouette and words per outcome — three channels, never colour alone
 * (`DESIGN.md` §4). The silhouettes are chosen to survive the greyscale screenshot
 * `docs/UI_PROCESS.md` §4 asks for: a tick, a triangle, a prohibition sign, a
 * refresh arrow and an octagon are five different shapes before they are five
 * different colours.
 */
const OUTCOME: Readonly<
  Record<InsightReading['kind'], { severity: Severity; icon: IconName; label: MessageKey }>
> = {
  published: { severity: 'low', icon: 'circle-check', label: 'm4.status.published' },
  fallback: { severity: 'high', icon: 'triangle-alert', label: 'm4.status.fallback' },
  // `ban`, deliberately not `triangle-alert`: nothing is broken and nothing is
  // coming. It is the same metaphor `FreshnessNote` uses for `requires_request`.
  budgetRefused: { severity: 'high', icon: 'ban', label: 'm4.status.budget' },
  superseded: { severity: 'medium', icon: 'refresh', label: 'm4.status.superseded' },
  unreadable: { severity: 'critical', icon: 'octagon-alert', label: 'm4.status.unreadable' },
};

function ConfidenceFigure({ confidence }: { readonly confidence: Confidence }): ReactNode {
  const { t } = useI18n();
  if (confidence.kind === 'absent') {
    // Never `0%`, never a blank, never a dash. D-22 shipped a withheld figure as
    // "0 giờ" on the screen of the person it was about; the type here makes the zero
    // unreachable and this is what a reader sees instead.
    return (
      <p className="wp-small" data-testid="m4-insight-confidence-absent">
        {t('m4.card.confidence.label')}:{' '}
        {t(
          confidence.absence === 'not_published'
            ? 'm4.card.confidence.absent.not_published'
            : confidence.absence === 'not_a_number'
              ? 'm4.card.confidence.absent.not_a_number'
              : 'm4.card.confidence.absent.out_of_range',
        )}
      </p>
    );
  }
  return (
    <p
      className="wp-small"
      data-testid="m4-insight-confidence"
      title={t('m4.card.confidence.help')}
    >
      {t('m4.card.confidence.label')}:{' '}
      <span className="wp-mono" data-testid="m4-insight-confidence-value">
        {confidence.value.toFixed(2)}
      </span>
    </p>
  );
}

/**
 * The region this whole file exists for.
 *
 * Plain `<section>`, plain `<ul>`. No disclosure element, no toggle, no clamp. The
 * heading is an `<h4>` inside the card's `<h3>` so the region is reachable by heading
 * navigation without being a landmark of its own.
 */
function Limitations({ caveats }: { readonly caveats: Caveats }): ReactNode {
  const { t } = useI18n();
  return (
    <section className="wp-ai-block" data-testid="m4-insight-limitations">
      <h4 className="wp-ai-block__title" data-testid="m4-insight-limitations-title">
        {t('m4.card.limitations.title')}
      </h4>
      <p className="wp-small" data-testid="m4-insight-limitations-why">
        {t('m4.card.limitations.why')}
      </p>
      <ul className="wp-list">
        {caveats.map((line, index) => (
          <li
            key={`${String(index)}-${line}`}
            data-testid={`m4-insight-limitation-${String(index)}`}
          >
            {line}
          </li>
        ))}
      </ul>
      {caveats.some(isCaveatSentinel) ? (
        <p className="wp-small" role="alert" data-testid="m4-insight-limitations-sentinel">
          {t('m4.card.limitations.sentinel')}
        </p>
      ) : null}
    </section>
  );
}

function StringList({
  items,
  emptyKey,
  testId,
}: {
  readonly items: readonly string[];
  readonly emptyKey: MessageKey;
  readonly testId: string;
}): ReactNode {
  const { t } = useI18n();
  if (items.length === 0) {
    // A sentence, never an empty list. An empty `<ul>` under a heading reads as a
    // rendering that did not finish, which is the one thing it must not read as on a
    // panel whose neighbouring variant is empty *by contract*.
    return (
      <p className="wp-small" data-testid={`${testId}-none`}>
        {t(emptyKey)}
      </p>
    );
  }
  return (
    <ul className="wp-list" data-testid={testId}>
      {items.map((item, index) => (
        <li key={`${String(index)}-${item}`}>{item}</li>
      ))}
    </ul>
  );
}

function EvidenceRefs({ refs }: { readonly refs: readonly EvidenceRef[] }): ReactNode {
  const { t } = useI18n();
  return (
    <ul className="wp-list" data-testid="m4-insight-evidence-refs">
      {refs.map((ref) => {
        const target = evidenceTarget(ref.refKind, ref.ref);
        return (
          <li key={ref.evidenceId} data-testid={`m4-insight-evidence-ref-${ref.evidenceId}`}>
            <span className="wp-mono">
              {ref.evidenceId} · {ref.refKind} · {ref.ref}
            </span>
            {target.kind === 'reachable' ? (
              // The request, not a link: this build has no screen addressed by the
              // source row's id, and a link to a page that does not exist is worse
              // than a printed request a reader can actually run.
              <>
                {' '}
                <span className="wp-small wp-muted">{t('m4.evidence.target.reachable')}</span>{' '}
                <code>{target.request}</code>
              </>
            ) : (
              <>
                {' '}
                <span
                  className="wp-small"
                  data-testid={`m4-insight-evidence-gap-${ref.evidenceId}`}
                >
                  {t(
                    target.kind === 'noEndpoint'
                      ? 'm4.evidence.target.noEndpoint'
                      : 'm4.evidence.target.notEmitted',
                    { needed: target.needed },
                  )}
                </span>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The variant-specific note. Five outcomes, five different things to do about them. */
function OutcomeNote({ reading }: { readonly reading: InsightReading }): ReactNode {
  const { t } = useI18n();
  if (reading.kind === 'published') return null;

  if (reading.kind === 'fallback') {
    return (
      <StatusNote
        severity="high"
        role="alert"
        title={t('m4.card.fallback.title')}
        testId="m4-insight-fallback"
      >
        <p>{t('m4.card.fallback.body')}</p>
        <p className="wp-small">{t('m4.card.fallback.markers')}</p>
        {reading.diagnosis === null ? (
          <p className="wp-small" data-testid="m4-insight-fallback-nodiagnosis">
            {t('m4.card.fallback.noDiagnosis')}
          </p>
        ) : (
          <p className="wp-small">
            {t('m4.card.fallback.diagnosis')}: <code>{reading.diagnosis}</code>
          </p>
        )}
      </StatusNote>
    );
  }

  if (reading.kind === 'budgetRefused') {
    return (
      <StatusNote
        severity="high"
        role="alert"
        title={t('m4.card.budget.title')}
        testId="m4-insight-budget"
      >
        <p>{t('m4.card.budget.body')}</p>
        {/* The two sentences that keep this apart from its neighbours. Separate test
            ids so an assertion cannot be satisfied by the note's own title, which is
            the neighbouring-element trap that has now caught three UI tasks. */}
        <p data-testid="m4-insight-budget-not-outage">{t('m4.card.budget.notOutage')}</p>
        <p data-testid="m4-insight-budget-not-empty">{t('m4.card.budget.notEmpty')}</p>
        <p className="wp-small">{t('m4.card.budget.fix')}</p>
        {reading.diagnosis === null ? null : (
          <p className="wp-small">
            {t('m4.card.fallback.diagnosis')}: <code>{reading.diagnosis}</code>
          </p>
        )}
      </StatusNote>
    );
  }

  if (reading.kind === 'superseded') {
    return (
      <StatusNote
        severity="medium"
        title={t('m4.card.superseded.title')}
        testId="m4-insight-superseded"
      >
        <p>{t('m4.card.superseded.body')}</p>
      </StatusNote>
    );
  }

  return (
    <StatusNote
      severity="critical"
      role="alert"
      title={t('m4.card.unreadable.title')}
      testId="m4-insight-unreadable"
    >
      <p data-testid="m4-insight-unreadable-reason">
        {reading.reason === 'no_status'
          ? t('m4.card.unreadable.no_status')
          : reading.reason === 'unknown_status'
            ? t('m4.card.unreadable.unknown_status', { status: reading.rawStatus ?? '' })
            : t('m4.card.unreadable.no_summary')}
      </p>
      <p className="wp-small">{t('m4.card.unreadable.caveatsKept')}</p>
    </StatusNote>
  );
}

export interface InsightCardProps {
  /**
   * The wire row, unread.
   *
   * Deliberately not an `InsightReading`: taking the raw record means `readInsight`
   * runs here and cannot be bypassed by a caller that felt like reading `summary`
   * itself. The chat turn reaches the same door through `insightRowFromChat`.
   */
  readonly row: Readonly<Record<string, unknown>>;
  readonly onOpenEvidence?: (insightId: string) => void;
  readonly headingId?: string;
}

export function InsightCard({ row, onOpenEvidence, headingId }: InsightCardProps): ReactNode {
  const { t } = useI18n();
  const reading = readInsight(row);
  const outcome = OUTCOME[reading.kind];
  const disagrees = markersDisagree(row);

  return (
    <article
      className="wp-card"
      data-testid="m4-insight-card"
      data-insight-kind={reading.kind}
      aria-labelledby={headingId}
    >
      <h3
        className="wp-card__title"
        {...(headingId === undefined ? {} : { id: headingId })}
        tabIndex={-1}
        data-testid="m4-insight-heading"
      >
        <Badge
          severity={outcome.severity}
          icon={outcome.icon}
          label={t(outcome.label)}
          testId="m4-insight-status"
        />
      </h3>

      {reading.kind === 'unreadable' ? null : (
        <p className="wp-small wp-muted" data-testid="m4-insight-meta">
          <span data-testid="m4-insight-type">
            {t('m4.filter.type')}:{' '}
            {reading.insightType === null
              ? t('m4.type.unknown')
              : t(TYPE_LABEL[reading.insightType] ?? 'm4.type.unknown')}
          </span>
          {reading.generatedAt === null ? null : (
            <>
              {' · '}
              {t('m4.card.generatedAt')}: <span className="wp-mono">{reading.generatedAt}</span>
            </>
          )}
          {reading.model === null ? null : (
            <>
              {' · '}
              {t('m4.card.model')}: <span className="wp-mono">{reading.model}</span>
            </>
          )}
          {reading.periodStart === null || reading.periodEnd === null ? null : (
            <>
              {' · '}
              {t('m4.card.period')}:{' '}
              <span className="wp-mono">
                {reading.periodStart} → {reading.periodEnd}
              </span>
            </>
          )}
          {reading.jobId === null ? null : (
            <>
              {' · '}
              {t('m4.card.jobId')}: <span className="wp-mono">{reading.jobId}</span>
            </>
          )}
        </p>
      )}

      <OutcomeNote reading={reading} />

      {disagrees ? (
        <StatusNote
          severity="critical"
          role="alert"
          title={t('m4.card.disagree.title')}
          testId="m4-insight-disagree"
        >
          <p>{t('m4.card.disagree.body')}</p>
        </StatusNote>
      ) : null}

      {reading.kind === 'unreadable' ? null : (
        <>
          {reading.question === null ? null : (
            <p className="wp-small wp-muted" data-testid="m4-insight-question">
              {t('m4.card.question')}: {reading.question}
            </p>
          )}
          <p className="wp-small wp-muted">{t('m4.card.summaryLabel')}</p>
          <p data-testid="m4-insight-summary">{reading.headline}</p>
          <ConfidenceFigure confidence={reading.confidence} />
        </>
      )}

      {/* Unconditional, and before the advice. Neither of those is a coincidence:
          see this file's header. */}
      <Limitations caveats={reading.caveats} />

      {reading.kind === 'published' ? (
        <>
          <p className="wp-small wp-muted">{t('m4.card.causes.title')}</p>
          <StringList
            items={reading.causes}
            emptyKey="m4.card.causes.none"
            testId="m4-insight-causes"
          />
          <p className="wp-small wp-muted">{t('m4.card.actions.title')}</p>
          <StringList
            items={reading.actions}
            emptyKey="m4.card.actions.none"
            testId="m4-insight-actions"
          />
          <p className="wp-small" data-testid="m4-insight-advisory">
            {t('m4.card.actions.advisory')}
          </p>
        </>
      ) : null}

      {reading.kind === 'unreadable' ? null : (
        <section className="wp-ai-block" data-testid="m4-insight-evidence">
          <h4 className="wp-ai-block__title">{t('m4.card.evidence.title')}</h4>
          {citesNothing(reading) ? (
            <p className="wp-small" role="alert" data-testid="m4-insight-cites-nothing">
              {t('m4.card.evidence.none')}
            </p>
          ) : (
            <>
              <EvidenceRefs refs={reading.evidence} />
              {onOpenEvidence === undefined || reading.id === '' ? null : (
                <button
                  type="button"
                  className="wp-button"
                  data-testid="m4-insight-evidence-open"
                  onClick={() => {
                    onOpenEvidence(reading.id);
                  }}
                >
                  {t('m4.card.evidence.open')}
                </button>
              )}
            </>
          )}
        </section>
      )}

      <p className="wp-small wp-muted" data-testid="m4-insight-verbatim">
        {t('m4.card.verbatim')}
      </p>
    </article>
  );
}
